import { apiClient } from './api';
import {
  StoryPromptSession,
  AnalyzeTextRequest,
  AnalyzeTextResponse,
  GeneratePromptsRequest,
  GeneratePromptsResponse,
  ConsistencyCheckRequest,
  ConsistencyCheckResponse,
  BatchIterateRequest,
  BatchIterateResponse,
  TextPartition,
  SectionPrompt,
} from '../models/storyPromptStudio';

const STORAGE_KEY = 'storyPromptStudioSessions';

class StoryPromptStudioService {
  // API calls
  async analyzeText(request: AnalyzeTextRequest): Promise<AnalyzeTextResponse> {
    const response = await apiClient.post('/api/story-partition/analyze', request);
    return response.data;
  }

  async generatePrompts(request: GeneratePromptsRequest): Promise<GeneratePromptsResponse> {
    const response = await apiClient.post('/api/story-prompt/generate-from-sections', request);
    return response.data;
  }

  async checkConsistency(request: ConsistencyCheckRequest): Promise<ConsistencyCheckResponse> {
    const response = await apiClient.post('/api/story-prompt/check-consistency', request);
    return response.data;
  }

  async iterateBatch(request: BatchIterateRequest): Promise<BatchIterateResponse> {
    const response = await apiClient.post('/api/story-prompt/iterate-batch', request);
    return response.data;
  }

  async startSession(request: {
    inputText: string;
    inputSource: string;
    visualStyle: any;
    selectedAgents?: string[];
    iterationMode: boolean;
  }): Promise<StoryPromptSession> {
    const response = await apiClient.post('/api/story-prompt/start-session', request);
    return response.data;
  }

  async getSession(sessionId: string): Promise<StoryPromptSession> {
    const response = await apiClient.get(`/api/story-prompt/${sessionId}`);
    return response.data;
  }

  // Agent execution for prompt iteration
  async executeIterationAgent(
    agentType: string,
    prompts: SectionPrompt[],
    feedback?: string
  ): Promise<any> {
    switch (agentType) {
      case 'prompt-improvement':
        return this.executePromptImprovement(prompts, feedback);
      case 'image-prompt':
        return this.executeImagePromptIterator(prompts, feedback);
      case 'character':
        return this.executeCharacterAgent(prompts);
      case 'scene':
        return this.executeSceneAgent(prompts);
      case 'storyboard':
        return this.executeStoryboardAgent(prompts);
      case 'prompt-evolution':
        return this.executePromptEvolution(prompts, feedback);
      case 'story-generator':
        return this.executeStoryGenerator(prompts, feedback);
      case 'screenplay':
        return this.executeScreenplayAgent(prompts, feedback);
      case 'abstract-thinker':
        return this.executeAbstractThinker(prompts, feedback);
      case 'creative-studio':
        return this.executeCreativeStudio(prompts, feedback);
      case 'comic-book':
        return this.executeComicBookAgent(prompts, feedback);
      case 'environmental-sim':
        return this.executeEnvironmentalAgent(prompts, feedback);
      case 'urban-culture':
        return this.executeUrbanCultureAgent(prompts, feedback);
      case 'architecture':
        return this.executeArchitectureAgent(prompts, feedback);
      case 'scientific':
        return this.executeScientificAgent(prompts, feedback);
      case 'fashion':
        return this.executeFashionAgent(prompts, feedback);
      case 'culinary':
        return this.executeCulinaryAgent(prompts, feedback);
      case 'music':
        return this.executeMusicAgent(prompts, feedback);
      default:
        // Fall back to generic prompt improvement
        return this.executePromptImprovement(prompts, feedback);
    }
  }

  private async executePromptImprovement(
    prompts: SectionPrompt[],
    feedback?: string
  ): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/agent/prompt/start', {
        prompt: prompt.imagePrompt,
        targetModel: 'flux',
        improvementType: 'visual_clarity',
        additionalInstructions: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeImagePromptIterator(
    prompts: SectionPrompt[],
    feedback?: string
  ): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/imageprompt/session/start', {
        initialPrompt: prompt.imagePrompt,
        style: prompt.styleModifiers.join(', '),
        additionalRequirements: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeCharacterAgent(prompts: SectionPrompt[]): Promise<any> {
    // Extract unique characters from all prompts
    const characters = new Set<string>();
    prompts.forEach(p => {
      p.metadata.characterConsistency.forEach(c => characters.add(c));
    });
    
    const promises = Array.from(characters).map(character =>
      apiClient.post('/api/character/start', {
        initialPrompt: character,
        name: character,
        role: 'story character',
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeSceneAgent(prompts: SectionPrompt[]): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/scene/start', {
        initialPrompt: prompt.imagePrompt,
        setting: prompt.metadata.settingConsistency,
        mood: prompt.metadata.moodProgression,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeStoryboardAgent(prompts: SectionPrompt[]): Promise<any> {
    const response = await apiClient.post('/api/storyboard/start', {
      initialPrompt: prompts.map(p => p.imagePrompt).join('\n\n'),
      panels: prompts.length,
      style: 'cinematic',
    });
    
    return response.data;
  }

  private async executePromptEvolution(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/prompt-evolution/start', {
        prompt: prompt.imagePrompt,
        targetOutput: 'visual_description',
        iterations: 3,
        userFeedback: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeStoryGenerator(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/story/start', {
        initialPrompt: prompt.imagePrompt,
        storyType: 'visual_narrative',
        additionalContext: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeScreenplayAgent(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/screenplay/start', {
        initialPrompt: prompt.imagePrompt,
        format: 'scene_description',
        additionalNotes: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeAbstractThinker(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/thinker/abstract/abstract-thinking/start', {
        initialQueryOrTopic: prompt.imagePrompt,
        depth: 'deep',
        temperature: 0.8,
        focusArea: feedback || 'visual conceptualization',
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeCreativeStudio(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    // Creative studio is a multi-agent workflow, so we'll use a simplified approach
    const promises = prompts.map(prompt =>
      apiClient.post('/api/creative-studio/quick-enhance', {
        prompt: prompt.imagePrompt,
        enhancementType: 'visual_richness',
        userGuidance: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeComicBookAgent(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/comic-book/start', {
        initialPrompt: prompt.imagePrompt,
        style: 'detailed_panel',
        additionalInstructions: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeEnvironmentalAgent(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/niche/environmental-simulation/start', {
        initialPrompt: prompt.imagePrompt,
        simulationType: 'natural_environment',
        detailLevel: 'high',
        userRequirements: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeUrbanCultureAgent(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/urban/urban-culture/start', {
        initialPrompt: prompt.imagePrompt,
        cultureAspect: 'street_scene',
        additionalContext: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeArchitectureAgent(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/niche/architecture-visualization/start', {
        initialPrompt: prompt.imagePrompt,
        visualizationType: 'environmental_context',
        additionalRequirements: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeScientificAgent(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/scientific/general/start', {
        initialPrompt: prompt.imagePrompt,
        scientificDomain: 'general',
        accuracy: 'high',
        userSpecifications: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeFashionAgent(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/fashion/start', {
        initialPrompt: prompt.imagePrompt,
        fashionAspect: 'clothing_details',
        additionalGuidance: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeCulinaryAgent(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/culinary/start', {
        initialPrompt: prompt.imagePrompt,
        culinaryFocus: 'food_presentation',
        additionalDetails: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  private async executeMusicAgent(prompts: SectionPrompt[], feedback?: string): Promise<any> {
    const promises = prompts.map(prompt =>
      apiClient.post('/api/music/start', {
        initialPrompt: prompt.imagePrompt,
        musicAspect: 'ambient_description',
        additionalInstructions: feedback,
      })
    );
    
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  }

  // Text processing utilities
  async processTextFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  async fetchTextFromUrl(url: string): Promise<string> {
    const response = await apiClient.post('/api/web-fetch', {
      url,
      extractText: true,
    });
    return response.data.text;
  }

  // Partition management
  mergePartitions(partitions: TextPartition[], indices: number[]): TextPartition[] {
    if (indices.length < 2) return partitions;
    
    const sorted = indices.sort((a, b) => a - b);
    const first = partitions[sorted[0]];
    const last = partitions[sorted[sorted.length - 1]];
    
    const merged: TextPartition = {
      id: `merged-${Date.now()}`,
      startIndex: first.startIndex,
      endIndex: last.endIndex,
      text: partitions.slice(sorted[0], sorted[sorted.length - 1] + 1)
        .map(p => p.text)
        .join(' '),
      analysis: {
        characters: Array.from(new Set(
          sorted.flatMap(i => partitions[i].analysis.characters)
        )),
        setting: first.analysis.setting,
        mood: first.analysis.mood,
        keyVisualElements: Array.from(new Set(
          sorted.flatMap(i => partitions[i].analysis.keyVisualElements)
        )),
        temporalContext: first.analysis.temporalContext,
        suggestedTransition: last.analysis.suggestedTransition,
      },
    };
    
    const result = [...partitions];
    sorted.reverse().forEach(i => result.splice(i, 1));
    result.splice(sorted[0], 0, merged);
    
    return result;
  }

  splitPartition(
    partition: TextPartition,
    splitPoint: number
  ): [TextPartition, TextPartition] {
    const text1 = partition.text.substring(0, splitPoint);
    const text2 = partition.text.substring(splitPoint);
    
    const partition1: TextPartition = {
      ...partition,
      id: `${partition.id}-1`,
      endIndex: partition.startIndex + splitPoint,
      text: text1,
    };
    
    const partition2: TextPartition = {
      ...partition,
      id: `${partition.id}-2`,
      startIndex: partition.startIndex + splitPoint,
      text: text2,
    };
    
    return [partition1, partition2];
  }

  // Local storage management
  saveSession(session: StoryPromptSession): void {
    const sessions = this.getAllSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  }

  getLocalSession(sessionId: string): StoryPromptSession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.id === sessionId) || null;
  }

  getAllSessions(): StoryPromptSession[] {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  deleteSession(sessionId: string): void {
    const sessions = this.getAllSessions();
    const filtered = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  }

  // Helper methods
  createLocalSession(title: string, text: string, source: string): StoryPromptSession {
    const wordCount = text.split(/\s+/).length;
    
    return {
      id: `story-prompt-${Date.now()}`,
      title,
      inputText: text,
      inputSource: source as any,
      partitions: [],
      characters: [],
      settings: [],
      visualStyle: {
        artStyle: 'illustration',
        colorPalette: 'mood-based',
        consistency: 'high',
        aspectRatio: '16:9',
        globalElements: [],  // Add required field
      },
      generatedPrompts: [],
      selectedAgents: ['prompt-improvement', 'image-prompt'],
      iterationMode: false,
      metadata: {
        totalWords: wordCount,
        totalPartitions: 0,
        averagePartitionSize: 0,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  addPromptToSession(session: StoryPromptSession, prompt: SectionPrompt): void {
    session.generatedPrompts.push(prompt);
    session.updatedAt = new Date().toISOString();
    this.saveSession(session);
  }

  // Export functionality
  exportSession(session: StoryPromptSession): string {
    const exportData = {
      session: {
        id: session.id,
        title: session.title,
        inputSource: session.inputSource,
        visualStyle: session.visualStyle,
        createdAt: session.createdAt,
      },
      partitions: session.partitions.map(p => ({
        text: p.text,
        analysis: p.analysis,
      })),
      prompts: session.generatedPrompts.map(p => ({
        partitionId: p.partitionId,
        imagePrompt: p.imagePrompt,
        styleModifiers: p.styleModifiers,
        negativePrompt: p.negativePrompt,
        consistencyMarkers: p.consistencyMarkers,
        metadata: p.metadata,
      })),
      characters: session.characters,
      settings: session.settings,
    };
    
    return JSON.stringify(exportData, null, 2);
  }

  exportPromptsAsMarkdown(session: StoryPromptSession): string {
    let markdown = `# ${session.title} - Visual Prompts\n\n`;
    
    session.generatedPrompts.forEach((prompt, index) => {
      const partition = session.partitions.find(p => p.id === prompt.partitionId);
      
      markdown += `## Section ${index + 1}\n\n`;
      
      if (partition) {
        markdown += `### Original Text\n${partition.text}\n\n`;
      }
      
      markdown += `### Visual Prompt\n${prompt.imagePrompt}\n\n`;
      
      if (prompt.styleModifiers.length > 0) {
        markdown += `**Style Modifiers:** ${prompt.styleModifiers.join(', ')}\n`;
      }
      
      if (prompt.negativePrompt) {
        markdown += `**Negative:** ${prompt.negativePrompt}\n`;
      }
      
      if (prompt.metadata.characterConsistency.length > 0) {
        markdown += `**Characters:** ${prompt.metadata.characterConsistency.join(', ')}\n`;
      }
      
      if (prompt.metadata.settingConsistency) {
        markdown += `**Setting:** ${prompt.metadata.settingConsistency}\n`;
      }
      
      markdown += '\n---\n\n';
    });
    
    return markdown;
  }

  // Batch operations
  exportPromptsForImageGeneration(prompts: SectionPrompt[]): any[] {
    return prompts.map(prompt => ({
      prompt: `${prompt.imagePrompt}, ${prompt.styleModifiers.join(', ')}`,
      negativePrompt: prompt.negativePrompt,
      metadata: {
        partitionId: prompt.partitionId,
        consistency: prompt.metadata,
      },
    }));
  }
}

export const storyPromptStudioService = new StoryPromptStudioService();